// routes.jsx
import { Navigate } from "react-router-dom";
import AdminLogin    from "../pages/auth/page";
import NotFound      from "../pages/NotFound";
import ProtectedRoute from "../route/ProtectedRoute";
import AdminLayout   from "../pages/dashboard/AdminLayout";

// ── View imports ──────────────────────────────────────────────
import OverviewView          from "../pages/dashboard/components/OverviewView";
import AnalyticsView         from "../pages/dashboard/views/AnalyticsView";
import TransactionsView      from "../pages/dashboard/views/TransactionsView";
import UsersView             from "../pages/dashboard/views/UsersView";
import KYCView               from "../pages/dashboard/views/KYCView";
import AssetsView            from "../pages/dashboard/views/AssetsView";
import WalletsView           from "../pages/dashboard/views/WalletsView";
import AlertsView            from "../pages/dashboard/views/AlertsView";
import SettingsView          from "../pages/dashboard/views/SettingsView";
import AgentDashboard        from "../pages/dashboard/views/AgentDashboard";
import NotificationsView     from "../pages/dashboard/views/NotificationsView";
import DataPlansView         from "../pages/dashboard/views/DataPlansView";
import MarketingView         from "../pages/dashboard/views/MarketingView";
import AppSettingsView       from "../pages/dashboard/views/AppSettingsView";
import ServicesView          from "../pages/dashboard/views/ServicesView";
import OnboardingView        from "../pages/dashboard/views/OnboardingView";
import AdminAccountsView     from "../pages/dashboard/views/AdminAccountsView";
import VTUProviderView       from "../pages/dashboard/views/VTUProviderView";
import CouponsView           from "../pages/dashboard/views/CouponsView";
import PromoCodesView        from "../pages/dashboard/views/PromoCodesView";
import MarketingSlidesView   from "../pages/dashboard/views/MarketingSlidesView";
import AdminCatalogView      from "../pages/dashboard/views/AdminCatalogView";
import AdminCompaniesView    from "../pages/dashboard/views/AdminCompaniesView";
import AdminSavingsSettingsView from "../pages/dashboard/views/AdminSavingsSettingsView";
import AdminRewardRulesView  from "../pages/dashboard/views/AdminRewardRulesView";
import AdminFraudView        from "../pages/dashboard/views/AdminFraudView";
import ProviderBalancesView  from "../pages/dashboard/views/ProviderBalancesView";
import AuditLogsView         from "../pages/dashboard/views/AuditLogsView";
import CountriesView         from "../pages/dashboard/views/CountriesView";

// ── Unauthorized page ─────────────────────────────────────────
function Unauthorized() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-center px-6">
      <div>
        <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <i className="ri-lock-line text-white/30 text-3xl" />
        </div>
        <p className="text-white font-bold text-lg mb-2">Access Denied</p>
        <p className="text-white/40 text-sm max-w-xs mx-auto">
          You do not have permission to view this page.
        </p>
        <a href="/auth/login"
          className="inline-block mt-6 px-6 py-2.5 bg-[#00C896] text-black font-bold rounded-xl text-sm">
          Back to Login
        </a>
      </div>
    </div>
  );
}

// ── Route config ──────────────────────────────────────────────
const routes = [
  {
    path: "/auth/login",
    element: <AdminLogin />,
  },
  {
    path: "/unauthorized",
    element: <Unauthorized />,
  },

  // ── Protected admin shell ─────────────────────────────────
  // AdminLayout renders Sidebar + Header + <Outlet />.
  // Every child route renders inside the Outlet — they all share the
  // same shell without remounting it on navigation.
  {
    path: "/",
    element: (
      <ProtectedRoute allowedRoles={["admin"]}>
        <AdminLayout />
      </ProtectedRoute>
    ),
    children: [
      // Default: redirect / → /dashboard
      { index: true, element: <Navigate to="/dashboard" replace /> },

      // ── Overview ────────────────────────────────────────────
      { path: "dashboard",        element: <OverviewView /> },
      { path: "analytics",        element: <AnalyticsView /> },

      // ── Operations ─────────────────────────────────────────
      { path: "transactions",     element: <TransactionsView /> },
      { path: "users",            element: <UsersView /> },
      { path: "kyc",              element: <KYCView /> },
      { path: "marketing",        element: <MarketingView /> },
      { path: "tickets",          element: <AgentDashboard /> },
      { path: "fraud",            element: <AdminFraudView /> },
      { path: "provider-balances",element: <ProviderBalancesView /> },

      // ── Finance ─────────────────────────────────────────────
      { path: "assets",           element: <AssetsView /> },
      { path: "wallets",          element: <WalletsView /> },
      { path: "data-plans",       element: <DataPlansView /> },
      { path: "providers",        element: <VTUProviderView /> },
      { path: "coupons",          element: <CouponsView /> },
      { path: "promo-codes",      element: <PromoCodesView /> },
      { path: "marketing-slides", element: <MarketingSlidesView /> },
      { path: "catalog",          element: <AdminCatalogView /> },
      { path: "companies",        element: <AdminCompaniesView /> },

      // ── System ──────────────────────────────────────────────
      // AlertsView is special — it receives live data from the layout
      // via React context (AdminAlertsContext). See AdminLayout.jsx.
      { path: "alerts",           element: <AlertsView /> },
      { path: "notifications",    element: <NotificationsView /> },
      { path: "services",         element: <ServicesView /> },
      { path: "onboarding",       element: <OnboardingView /> },
      { path: "app-settings",     element: <AppSettingsView /> },
      { path: "savings-settings", element: <AdminSavingsSettingsView /> },
      { path: "reward-rules",     element: <AdminRewardRulesView /> },
      { path: "audit-logs",       element: <AuditLogsView /> },
      { path: "countries",         element: <CountriesView /> },
      { path: "settings",         element: <SettingsView /> },
      { path: "accounts",         element: <AdminAccountsView /> },

      // Catch-all inside the shell → 404
      { path: "*",                element: <NotFound /> },
    ],
  },

  // Catch-all outside the shell → 404
  { path: "*", element: <NotFound /> },
];

export default routes;